import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Save, Mail, MessageSquare, Pencil, Copy } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem("adminToken") || ""}` });

/**
 * Email + SMS template CRUD with Duplicate.
 * Extracted from the legacy AdminNotifications "Templates" tab so the new
 * Communications hub can reuse it. AdminNotifications no longer renders this —
 * Templates live exclusively under /admin/communications?tab=templates now.
 */
export function TemplatesTab() {
  const [kind, setKind] = useState("email");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/${kind}-templates`, { headers: headers() });
      setRows(res.data.templates || []);
    } catch { toast.error("Failed to load templates"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [kind]);

  const save = async (tpl) => {
    try {
      if (tpl.id) await axios.patch(`${API}/admin/${kind}-templates/${tpl.id}`, tpl, { headers: headers() });
      else await axios.post(`${API}/admin/${kind}-templates`, tpl, { headers: headers() });
      setEditing(null);
      load();
      toast.success("Saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      await axios.delete(`${API}/admin/${kind}-templates/${id}`, { headers: headers() });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const duplicate = async (id) => {
    try {
      await axios.post(`${API}/admin/${kind}-templates/${id}/duplicate`, {}, { headers: headers() });
      toast.success("Template duplicated");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Duplicate failed"); }
  };

  return (
    <div data-testid="templates-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex gap-2">
          {["email", "sms"].map(k => (
            <button key={k} onClick={() => setKind(k)} data-testid={`templates-kind-${k}`}
              className={`flex items-center gap-2 px-3 py-1.5 text-[11px] uppercase tracking-wider border ${kind === k ? "bg-[#9A7B4F] text-white border-[#9A7B4F]" : "bg-white text-[#676664] border-[#282828]/12"}`}>
              {k === "email" ? <Mail className="w-3.5 h-3.5" /> : <MessageSquare className="w-3.5 h-3.5" />}
              {k}
            </button>
          ))}
        </div>
        <button onClick={() => setEditing({ key: "", label: "", subject: "", body: "", tokens: [] })} data-testid="new-template-btn"
          className="flex items-center gap-2 px-3 py-2 text-[11px] uppercase tracking-wider bg-[#9A7B4F] text-white hover:bg-[#8B6B3F]">
          <Plus className="w-3.5 h-3.5" /> New Template
        </button>
      </div>

      {loading ? <p className="text-[12px] text-[#676664]/60">Loading…</p> : (
        <div className="space-y-2">
          {rows.map(tpl => (
            <div key={tpl.id} className="bg-white border border-[#282828]/8 p-3" data-testid={`template-row-${tpl.key}`}>
              <div className="flex justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] text-[#282828] font-medium">
                    {tpl.label || tpl.name} <span className="text-[10px] text-[#676664]/50">({tpl.key})</span>
                    {tpl.editable === false && <span className="ml-2 text-[10px] uppercase tracking-wider text-[#9A7B4F]/80">read-only</span>}
                  </p>
                  {kind === "email" && <p className="text-[11px] text-[#676664]/70 mt-1">Subject: {tpl.subject || "—"}</p>}
                  <p className="text-[11px] text-[#676664]/60 mt-1 whitespace-pre-line">
                    {(tpl.body || "").slice(0, 200)}{(tpl.body || "").length > 200 ? "…" : ""}
                  </p>
                </div>
                <div className="flex gap-2 items-start whitespace-nowrap">
                  <button onClick={() => duplicate(tpl.id)} data-testid={`duplicate-template-${tpl.key}`}
                    className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-wider border border-[#282828]/12 text-[#676664] hover:bg-[#FAF8F0]">
                    <Copy className="w-3 h-3" /> Duplicate
                  </button>
                  <button onClick={() => setEditing(tpl)} data-testid={`edit-template-${tpl.key}`}
                    className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-wider border border-[#282828]/12 text-[#676664] hover:bg-[#FAF8F0]"
                    disabled={tpl.editable === false} title={tpl.editable === false ? "This template is read-only" : ""}>
                    <Pencil className="w-3 h-3" /> Edit
                  </button>
                  <button onClick={() => remove(tpl.id)} data-testid={`delete-template-${tpl.key}`}
                    disabled={tpl.editable === false}
                    title={tpl.editable === false ? "Read-only templates cannot be deleted — duplicate first to customise" : ""}
                    className="text-[#B85C5C] hover:opacity-70 px-2 py-1 disabled:opacity-30 disabled:cursor-not-allowed">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
          {rows.length === 0 && (
            <p className="text-[12px] text-[#676664]/50 italic text-center py-8">No {kind} templates yet.</p>
          )}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white max-w-2xl w-full p-5 overflow-y-auto max-h-[90vh]" onClick={e => e.stopPropagation()} data-testid="template-editor">
            <h3 className="text-[16px] font-semibold mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {editing.id ? "Edit" : "New"} {kind.toUpperCase()} template
            </h3>
            <label className="block text-[10px] uppercase tracking-wider text-[#676664]/60 mb-1">Key (snake_case)</label>
            <input placeholder="customer_followup_v2" value={editing.key} onChange={e => setEditing({ ...editing, key: e.target.value })}
              className="w-full border border-[#282828]/12 px-3 py-2 text-[13px] mb-3 font-mono" data-testid="template-key" />
            <label className="block text-[10px] uppercase tracking-wider text-[#676664]/60 mb-1">Label (human readable)</label>
            <input placeholder="Customer follow-up — v2" value={editing.label || editing.name || ""} onChange={e => setEditing({ ...editing, label: e.target.value })}
              className="w-full border border-[#282828]/12 px-3 py-2 text-[13px] mb-3" data-testid="template-label" />
            {kind === "email" && (
              <>
                <label className="block text-[10px] uppercase tracking-wider text-[#676664]/60 mb-1">Subject</label>
                <input placeholder="Subject" value={editing.subject || ""} onChange={e => setEditing({ ...editing, subject: e.target.value })}
                  className="w-full border border-[#282828]/12 px-3 py-2 text-[13px] mb-3" data-testid="template-subject" />
              </>
            )}
            <label className="block text-[10px] uppercase tracking-wider text-[#676664]/60 mb-1">
              Body
              <span className="ml-2 normal-case text-[#676664]/40">Tokens: [Customer Name], [Outfit Name], [Event Date], [Date], [Time], [Contact Number]</span>
            </label>
            <textarea rows={kind === "email" ? 10 : 5} placeholder="Hi [Customer Name],&#10;&#10;…" value={editing.body || ""}
              onChange={e => setEditing({ ...editing, body: e.target.value })}
              className="w-full border border-[#282828]/12 px-3 py-2 text-[13px] font-mono mb-3" data-testid="template-body" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="px-3 py-2 text-[11px] uppercase tracking-wider border border-[#282828]/12">Cancel</button>
              <button onClick={() => save(editing)} data-testid="save-template-btn" className="flex items-center gap-2 px-3 py-2 text-[11px] uppercase tracking-wider bg-[#9A7B4F] text-white hover:bg-[#8B6B3F]">
                <Save className="w-3.5 h-3.5" /> Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
